import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ModalCore, { ModalType } from '../Modal/ModalCore';
import { FundListFilterConfig } from './useFundListFilter';
import { getKeyboardKey } from '../wcag/getKeyboardKey';
import { Button } from '../Button/Button';
import { CheckboxesModalList } from './components/CheckboxesModalList/CheckboxesModalList';
import { FormControlLabel } from '../form/FormControlLabel/FormControlLabel';
import { Checkbox } from '../Checkbox/Checkbox';
import clsx from 'clsx';
import { Typography } from '../Typography/Typography';
import { useTheme} from '../styles/styles';
import { MobileAppTopBar } from '../MobileAppTopBar/MobileAppTopBar';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { useUniqueWcagValues } from 'common-react';

import useStyles from './FundList.style';

export interface FundListFilterModalProps {
  resultsNumber: number;
  filterConfig: FundListFilterConfig;
  onCloseModal: () => void;
  hasAdequacyWithTargetMarketSelect: boolean;
  showWalletCheckbox: boolean;
}

const FundListFilterModal: React.FC<FundListFilterModalProps> = ({
  resultsNumber,
  filterConfig,
  onCloseModal,
  hasAdequacyWithTargetMarketSelect,
  showWalletCheckbox
}) => {
  const theme = useTheme();
  const isDarkTheme = false;
  const c = useStyles({ isDarkTheme });
  const [t] = useTranslation('components');
  const [tt] = useTranslation();
  const modalBackButtonRef = useRef<HTMLButtonElement>(null);
  const clearFiltersButtonRef = useRef<HTMLButtonElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const { getUniqueValue } = useUniqueWcagValues();
  const walletDescriptionId = getUniqueValue('wallet-filter-description');
  const esgDescriptionId = getUniqueValue('esg-filter-description');
  const adequacyDescriptionId = getUniqueValue('adequacy-filter-description');
  const checkboxAutoFilterDescription = t('fund-list.checkbox-auto-filter-description');

  useEffect(() => {
    if (modalBackButtonRef.current) {
      setTimeout(() => {
        modalBackButtonRef.current?.focus();
      }, 0);
    }
  }, []);

  const handleSubmitButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const { isTabKey } = getKeyboardKey(event.nativeEvent);

    if (event.shiftKey && isTabKey) {
      return;
    }

    if (isTabKey && !filterConfig.isAnyFilter) {
      event.stopPropagation();
      event.preventDefault();
      modalBackButtonRef.current?.focus();
    } else if (isTabKey && filterConfig.isAnyFilter) {
      event.preventDefault();
      event.stopPropagation();
      clearFiltersButtonRef.current?.focus();
    } else {
      return;
    }
  }

  const handleClearFiltersButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const { isTabKey } = getKeyboardKey(event.nativeEvent);

    event.stopPropagation();

    if (isTabKey && event.shiftKey) {
      event.preventDefault();
      submitButtonRef.current?.focus();
    } else if (isTabKey) {
      event.preventDefault();
      modalBackButtonRef.current?.focus();
    } else {
      return;
    }
  }

  const handleModalBackButtonKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const { isTabKey } = getKeyboardKey(event.nativeEvent);
    if (isTabKey && event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      filterConfig.isAnyFilter ? clearFiltersButtonRef.current?.focus() : submitButtonRef.current?.focus();
    }
  }

  const filterSections = [
    filterConfig.risk,
    filterConfig.fundType,
    filterConfig.umbrella,
    filterConfig.horizon,
  ];

  return (
    <ModalCore
      handleClose={onCloseModal}
      disableFocusTrap
      ref={modalBackButtonRef}
      onBackButtonKeyDown={handleModalBackButtonKeyDown}
      type={ModalType.withHeader}
      titleType="left"
      title={tt('filtration')}
      renderMobileTopBar={() => <MobileAppTopBar backgroundColor={theme.color.greys[0]} zIndex={9005} />}
    >
      <div className={c.mobilFilterInnerWrapper}>
        <div>
          <ul className={c.mobileFilterList}>
            {filterSections.map(section => (
              <li key={section.label} className={c.checkboxWrapperItem}>
                <CheckboxesModalList {...section} title={section.label} />
              </li>
            ))}
            <li className={clsx(c.checkboxWrapperItem, c.checkboxWrapperItemShow)}>
              <Typography fontWeight='bold'>{tt('show')}</Typography>
            </li>
            {hasAdequacyWithTargetMarketSelect && (
              <li className={clsx(
                c.checkboxWrapperItem,
                c.checkboxWrapperItemCheckbox,
                c.checkboxWrapperItemCheckboxAdditional)}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...filterConfig.adequacyWithTargetMarket}
                      onChange={(_, isChecked) =>
                        filterConfig.adequacyWithTargetMarket.onChange(
                          isChecked,
                        )
                      }
                      inputProps={{
                        'aria-describedby': adequacyDescriptionId,
                      }}
                    />
                  }
                  label={t(
                    "fund-list.options.adequacy-with-target-market.label",
                  )}
                />
                <VisuallyHidden id={adequacyDescriptionId}>
                  {checkboxAutoFilterDescription}
                </VisuallyHidden>
              </li>
            )}
            {showWalletCheckbox && (
              <li className={clsx(
                c.checkboxWrapperItem,
                c.checkboxWrapperItemCheckbox,
                c.checkboxWrapperItemCheckboxAdditional)}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...filterConfig.wallet}
                      onChange={(_, isChecked) =>
                        filterConfig.wallet.onChange(isChecked)
                      }
                      inputProps={{
                        'aria-describedby': walletDescriptionId,
                      }}
                    />
                  }
                  label={t("fund-list.options.wallet.label")}
                />
                <VisuallyHidden id={walletDescriptionId}>
                  {checkboxAutoFilterDescription}
                </VisuallyHidden>
              </li>
            )}
            <li className={clsx(
              c.checkboxWrapperItem,
              c.checkboxWrapperItemCheckbox,
              c.checkboxWrapperItemCheckboxAdditional)}>
              <FormControlLabel
                control={
                  <Checkbox
                    {...filterConfig.esg}
                    onChange={(_, isChecked) =>
                      filterConfig.esg.onChange(isChecked)
                    }
                    inputProps={{
                      'aria-describedby': esgDescriptionId,
                    }}
                  />
                }
                label={t("fund-list.options.esg.label")}
              />
              <VisuallyHidden id={esgDescriptionId}>
                {checkboxAutoFilterDescription}
              </VisuallyHidden>
            </li>
          </ul>
        </div>
        <div>
          <div className={c.mobileFilterButtonsBox}>
            <Button onClick={onCloseModal} onKeyDown={handleSubmitButtonKeyDown} ref={submitButtonRef}>
              {filterConfig.isAnyFilter ? `${t('fund-list.show-results')} (${resultsNumber})` : t('fund-list.show-results')}
            </Button>
            {filterConfig.isAnyFilter && <Button
              buttonType="secondary"
              onClick={filterConfig.clearFilters}
              ref={clearFiltersButtonRef}
              onKeyDown={handleClearFiltersButtonKeyDown}
            >
              {t('fund-list.clear-filter')}
            </Button>}
          </div>
        </div>
      </div>
    </ModalCore>
  );
};

export default FundListFilterModal;
